import { getRuntimeConfig } from "../core";
import { visionAblyChannelName } from "./messaging";

export type DirectHistoryFetcher = () => Promise<{ name: string; data: unknown } | null>;

/**
 * Fetches the last message on a channel via the Ably REST API (`https://rest.ably.io`)
 * using plain `fetch()`, bypassing the Ably SDK's routing.
 *
 * Used on screens where we want history reads to skip toxiproxy even when realtime
 * traffic is proxied — e.g. the pairing handshake on `/device-pairing`.
 */
export function createDirectHistoryFetcher(apiKey: string, channelName: string): DirectHistoryFetcher {
	return async () => {
		const url = `https://rest.ably.io/channels/${encodeURIComponent(channelName)}/messages?limit=1&direction=backwards`;

		const res = await fetch(url, {
			headers: { Authorization: `Basic ${btoa(apiKey)}` },
		});

		if (!res.ok) {
			getRuntimeConfig().logger.warning("INFO", `Direct history fetch failed: ${res.status} ${res.statusText}`);
			return null;
		}

		const items: unknown = await res.json();
		if (!Array.isArray(items) || items.length === 0) return null;

		const raw = items[0] as { name?: string; data?: unknown; encoding?: string };

		let data = raw.data;
		if (raw.encoding === "json" && typeof data === "string") {
			try {
				data = JSON.parse(data);
			} catch {
				/* keep as string */
			}
		}

		return { name: raw.name ?? "", data };
	};
}

export function createVisionDirectHistoryFetcher(
	apiKey: string,
	sessionId: string | null,
): DirectHistoryFetcher | null {
	const channelName = visionAblyChannelName(sessionId);
	if (!channelName) return null;
	return createDirectHistoryFetcher(apiKey, channelName);
}
